import axiosInstanceBuilder from "../config/axios/axiosInstanceBuilder";
import { useFetch } from "../config/axios/useFetch";
import { checkIfAuthenticated } from "../../utils/checkAuthentication";

const baseUrl = process.env.REACT_APP_API_DEV_URL;

/**
 * Custom hook for handling exam answers API calls
 * @param {string} examInstanceId - The exam instance ID
 * @param {string} ubaToken - UBA token for non-authenticated users (optional)
 */
export const useExamAnswers = (examInstanceId, ubaToken = null) => {
  const createAxiosPromise = async (params) => {
    const userAuthenticated = await checkIfAuthenticated();
    let endpoint;
    let axiosInstance;

    if (userAuthenticated) {
      endpoint = `/exam-instances/${examInstanceId}/answers`;
      axiosInstance = axiosInstanceBuilder(baseUrl); // It adds headers
    } else if (ubaToken) {
      endpoint = `/uba/exam-instances/${examInstanceId}/answers?tk=${ubaToken}`;
      axiosInstance = axiosInstanceBuilder(baseUrl, false); // It does not add headers
    } else {
      throw new Error("Authentication required to access answers.");
    }

    return axiosInstance.post(endpoint, params);
  };

  const { fetchApi } = useFetch(createAxiosPromise, false);

  /**
   * Check if current user is authenticated (for error handling decisions)
   */
  const isUserAuthenticated = async () => {
    return await checkIfAuthenticated();
  };

  /**
   * Submit an answer for the current question
   * @param {Object} answerData - Answer data object
   * @param {string|null} answerData.hash_id - Current question hash ID
   * @param {string|null} answerData.exam_request_token - Exam request token
   * @param {boolean} answerData.fetch_next_question - Whether to fetch next question
   * @param {string} answerData.open_response - Code response for coding questions
   * @param {string} answerData.response_language - Programming language for coding questions
   * @param {Array} answerData.selected_choices_ids - Selected choice IDs for multiple choice questions
   * @returns {Promise<Object>} API response with question data or error information
   */
  const submitAnswer = async (answerData) => {
    const params = {
      csrf_token: null,
      hash_id: answerData.hash_id ?? null,
      exam_request_token: answerData.exam_request_token ?? null,
      // Use nullish coalescing so an intentional `false` is not coerced to `true`.
      fetch_next_question: answerData.fetch_next_question ?? true,
      ...answerData,
    };

    try {
      const response = await fetchApi(params);

      return response;
    } catch (error) {
      return {
        data: null,
        error,
        isPending: false,
        hasFinished: true,
      };
    }
  };

  /**
   * Load the initial question for an exam (used when starting an exam)
   * @returns {Promise<Object>} API response with initial question data or error information
   */
  const loadInitialQuestion = async () => {
    return submitAnswer({
      hash_id: null,
      exam_request_token: null,
      fetch_next_question: true,
      selected_choices_ids: [],
      open_response: "",
      response_language: "python",
    });
  };

  return {
    submitAnswer,
    loadInitialQuestion,
    isUserAuthenticated,
  };
};
